/* eslint-disable camelcase */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { api_with_method } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import { day_format_year, time_format } from '../../../../constants/index';
import {
  build_gift_card_rows,
  format_send_date,
  gift_cards_error_message,
  is_gift_cards_body
} from './gift_cards_utils';
import './gift_cards.css';

const HEADERS = [
  'Direction',
  'Other party',
  'Status',
  'Send date',
  'Amount',
  'Amount left',
  'Gift code',
  'Created'
];

const RETRY_BUTTON_STYLE = {
  border: '1px solid var(--brand-primary)',
  color: 'var(--brand-primary)',
  background: 'var(--surface-card)'
};

const format_date = (value, iana_timezone, format) => {
  if (!value) return '—';
  const date = moment(value);
  return date.isValid() ? date.tz(iana_timezone).format(format) : '—';
};

function GiftCards({ student_uuid, iana_timezone }) {
  const [rows, set_rows] = useState([]);
  const [loading, set_loading] = useState(false);
  const [error, set_error] = useState('');
  // A newer load supersedes older ones, so a slow answer for another profile is ignored.
  const request_id_ref = useRef(0);

  const load_gift_cards = useCallback(() => {
    if (!student_uuid) return;
    const request_id = request_id_ref.current + 1;
    request_id_ref.current = request_id;
    set_error('');
    set_loading(true);
    const url = get_api_url(
      `get_student_gift_cards?student_uuid=${encodeURIComponent(student_uuid)}`
    );
    api_with_method('get', url, null, true)
      .then((response) => {
        if (request_id !== request_id_ref.current) return;
        const body = response?.data;
        if (!is_gift_cards_body(body)) {
          set_rows([]);
          set_error(gift_cards_error_message({ response: { data: body } }));
          return;
        }
        set_rows(build_gift_card_rows(body.data));
      })
      .catch((e) => {
        if (request_id !== request_id_ref.current) return;
        set_rows([]);
        set_error(gift_cards_error_message(e));
      })
      .finally(() => {
        if (request_id !== request_id_ref.current) return;
        set_loading(false);
      });
  }, [student_uuid]);

  useEffect(() => {
    load_gift_cards();
  }, [load_gift_cards]);

  if (!student_uuid) return null;

  const render_body = () => {
    if (loading) return <div style={{ color: 'var(--text-muted)' }}>Loading gift cards…</div>;
    if (error) {
      return (
        <div role="alert" style={{ color: 'var(--state-error)' }}>
          <p className="mb-2">{error}</p>
          <button
            className="px-3 py-1 rounded-md text-sm font-semibold"
            style={RETRY_BUTTON_STYLE}
            type="button"
            onClick={load_gift_cards}
          >
            Retry
          </button>
        </div>
      );
    }
    if (rows.length === 0) {
      return <div style={{ color: 'var(--text-muted)' }}>No gift cards sent or received.</div>;
    }
    return (
      <div className="gift-cards-scroll">
        <table className="gift-cards-table">
          <thead>
            <tr>
              {HEADERS.map((header) => (
                <th key={header}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} data-testid="gift-card-row">
                <td>{row.direction}</td>
                <td className="gift-cards-wrap">{row.other_party}</td>
                <td>{row.status_label}</td>
                <td>{format_send_date(row.message_date)}</td>
                <td>{row.amount_text}</td>
                <td>{row.amount_left_text}</td>
                <td className="gift-cards-wrap">{row.gift_code}</td>
                <td>
                  <div>{format_date(row.created_date, iana_timezone, day_format_year)}</div>
                  <div>{format_date(row.created_date, iana_timezone, time_format)}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div style={{ marginTop: '50px' }}>
      Gift cards:
      {render_body()}
    </div>
  );
}

GiftCards.propTypes = {
  student_uuid: PropTypes.string,
  iana_timezone: PropTypes.string
};

GiftCards.defaultProps = {
  student_uuid: '',
  iana_timezone: 'UTC'
};

export default GiftCards;
